import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { MenuItem, Select } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {
    BigModal,
    FlexDiv,
    TextDefault,
    TextTiny,
    ThemedButton,
    Tag,
    palette,
} from '@awarego-ui/components';
import { WEBHOOK_TYPE_OPTIONS } from './WebhookFormFields';

// Monospaced block for the request/response dump. Scrolls on its own so a
// chatty receiver can't stretch the dialog.
const preStyle = {
    margin: 0,
    padding: '8px 12px',
    maxHeight: 220,
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    background: 'rgba(0, 0, 0, 0.04)',
    borderRadius: 4,
};

const asText = (value) =>
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);

/**
 * "Test webhook" dialog: pick an event, send it, see exactly what went out and
 * what came back.
 *
 * Deliberately unaware of whether this is a company or a partner webhook - the
 * container passes `onSend`, which calls the right service. Both sides get the
 * same dialog that way.
 */
function WebhookTestDialog({ isOpen, webhook, onClose, onSend }) {
    const [eventName, setEventName] = useState(WEBHOOK_TYPE_OPTIONS[0].id);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // Each opening starts clean: showing the previous webhook's response next
    // to a new webhook's name would be actively misleading.
    useEffect(() => {
        if (!isOpen) return;
        setResult(null);
        setError(null);
        setSending(false);
        // Default to something this webhook is actually subscribed to, so the
        // obvious first click tests the case the integrator cares about.
        //
        // eventName is an array for anything created since the multi-select,
        // but the routes only started normalising it then - a row saved before
        // that can still hold a bare string, and calling .find on it would
        // throw as the dialog opens.
        const subscribedRaw = webhook && webhook.definition?.eventName;
        const subscribed = Array.isArray(subscribedRaw)
            ? subscribedRaw
            : subscribedRaw
            ? [subscribedRaw]
            : [];
        const firstReal = subscribed.find((name) =>
            WEBHOOK_TYPE_OPTIONS.some((option) => option.id === name)
        );
        setEventName(firstReal || WEBHOOK_TYPE_OPTIONS[0].id);
    }, [isOpen, webhook]);

    const send = async () => {
        if (sending) return;
        setSending(true);
        setError(null);
        setResult(null);
        try {
            setResult(await onSend(eventName));
        } catch (e) {
            // A failing receiver is a normal outcome reported inside `result`;
            // reaching here means the call itself failed (offline, refused
            // because the webhook is disabled, no longer ours, and so on).
            setError(
                (e && e.response && e.response.data && e.response.data.error) ||
                    (e && e.message) ||
                    'Test request failed'
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <BigModal
            ariaHideApp={false}
            isOpen={isOpen}
            width={640}
            onRequestClose={onClose}
            heading="Test Webhook"
            modalContent={
                <FlexDiv column>
                    <TextDefault mb={4}>
                        Sends a sample event to{' '}
                        <strong>{webhook && webhook.url}</strong>. The delivery
                        is recorded as a test and is never retried.
                    </TextDefault>

                    <TextTiny mb={4} bold>
                        Choose an event to test
                    </TextTiny>
                    <Select
                        value={eventName}
                        onChange={(e) => setEventName(e.target.value)}
                        disabled={sending}
                        fullWidth
                    >
                        {WEBHOOK_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>

                    {error && (
                        <TextDefault mt={16} color={palette.vibrant.red}>
                            {error}
                        </TextDefault>
                    )}

                    {sending && (
                        <FlexDiv mt={16} justifyCenter>
                            <CircularProgress />
                        </FlexDiv>
                    )}

                    {result && (
                        <FlexDiv column mt={16}>
                            <FlexDiv alignCenter mb={8}>
                                <Tag
                                    text={
                                        result.success
                                            ? 'Successfully received OK response from the webhook'
                                            : 'The webhook did not accept the event'
                                    }
                                    color={
                                        result.success
                                            ? palette.evergreen.aspargus
                                            : palette.vibrant.red
                                    }
                                    backgroundColor={
                                        result.success
                                            ? palette.evergreen.mintLight
                                            : palette.pastel.red
                                    }
                                />
                            </FlexDiv>

                            <TextTiny bold mb={4}>
                                Request sent
                            </TextTiny>
                            <pre style={preStyle}>
                                {`POST ${result.request.url}\n\n` +
                                    `${asText(result.request.headers)}\n\n` +
                                    `${asText(result.request.body)}`}
                            </pre>

                            <TextTiny bold mt={16} mb={4}>
                                Response received
                            </TextTiny>
                            <pre style={preStyle}>
                                {`HTTP ${result.response.status}${
                                    result.response.statusText
                                        ? ` ${result.response.statusText}`
                                        : ''
                                } (${result.response.durationMs} ms)\n\n` +
                                    `${asText(result.response.headers)}\n\n` +
                                    `${asText(result.response.body)}`}
                            </pre>
                        </FlexDiv>
                    )}
                </FlexDiv>
            }
            modalFooterContent={
                <>
                    <ThemedButton
                        mode="primary"
                        text={sending ? 'Sending' : 'Test URL'}
                        $saving={sending}
                        $center={true}
                        disabled={sending}
                        onClick={send}
                    />
                    <ThemedButton
                        mode="secondary"
                        onClick={onClose}
                        text="Close"
                    />
                </>
            }
        />
    );
}

export default observer(WebhookTestDialog);
